import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useReactiveVar } from '@apollo/client'
import { GridApi, GridReadyEvent, SelectionChangedEvent } from 'ag-grid-community'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import { Close, SearchOutlined } from '@mui/icons-material'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from '@mui/material'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AgGridReact } from 'ag-grid-react'
import clsx from 'clsx'
import {
  isAssignmentsColumnHeaderRightClickMenuVar,
  isColumnHeaderRightClickMenuVar,
} from 'cache/adv-filtering'
import { setAlertQueue } from 'cache/alerts'
import {
  approverGroupsListVar,
  hasUnsavedChangesVar,
  selectedBackupApproverGroupsVar,
  setHasUnsavedChanges,
  setSelectedApproverGroups,
} from 'cache/manage-backup'
import { AgContextMenuContainer, defaultColDef } from 'components/AgGrid'
import Alert from 'components/AlertContextProvider/components/Alert'
import errorMessages from 'constants/errorMessages'
import { useGetApproverGroupsByUser } from 'hooks/use-approver-groups-by-user'
import useAuthHook from 'hooks/use-auth'
import { useGetBackupApproverByUser } from 'hooks/use-backup-approvers-by-user'
import { useEmployeeLookupWithPhFlag } from 'hooks/use-employee-lookupwithphflag'
import { useSaveBackupApprovers } from 'hooks/use-save-backup-approver'
import { useStyles } from 'styles/material-styles/manageBackUpPage'
import { MessageType } from 'ts/enums/messageType.enums'
import { frameworkComponents } from './ApproverGroupTable/agGridConfig/agGridConfig'
import approverGroupColumnDefs from './ApproverGroupTable/agGridConfig/columnDefs'
import { frameworkComponents as assignmentsFrameworkComponentsBase } from './AssignmentsTable/agGridConfig/agGridConfig'
import assignmentsColumnsDefs from './AssignmentsTable/agGridConfig/columnDefs'
import DeleteCellRenderer from './AssignmentsTable/DeleteCellRenderer/DeleteCellRenderer'
import DeleteHeaderRenderer from './AssignmentsTable/DeleteHeaderRenderer/DeleteHeaderRenderer'
import {
  SelectedBackupApproverGroup as _SelectedBackupApproverGroup,
  addFieldsToRows,
  buildSelectedBackupApproverGroupsInput,
  formatDateLocal,
  formatEmployeeDisplay,
  getTodayDateString,
  parseISODate,
  processSearchString,
  removeSpacesAfterComma,
  toProperCase,
} from './helperFns'
import './styles.module.scss'

const TABLE_STATE_KEY = 'manageBackUpPage'

const getMaxEndDate = (beginDateString: string) => {
  const beginDate = parseISODate(beginDateString)
  const maxEndDate = new Date(beginDate)
  maxEndDate.setDate(maxEndDate.getDate() + 29)
  return maxEndDate.toISOString().slice(0, 10)
}

function shouldClearEndDate(
  newBeginDate: string,
  endDate: string,
  getMaxEndDate: (d: string) => string
): boolean {
  const beginDateObj = parseISODate(newBeginDate)
  const endDateObj = parseISODate(endDate)
  const maxEndDateObj = parseISODate(getMaxEndDate(newBeginDate))
  return endDateObj < beginDateObj || endDateObj > maxEndDateObj
}

const ManageBackUpPage = () => {
  const { classes } = useStyles()
  const [gridApi, setGridApi] = useState<GridApi | null>(null)
  const [assignmentsGridApi, setAssignmentsGridApi] = useState<GridApi | null>(null)
  const [isCheckboxChecked, setIsCheckboxChecked] = useState(true)
  const { saveBackupApprovers } = useSaveBackupApprovers()
  const gridRef = useRef<AgGridReact>(null)
  const assignmentsTableRef = useRef<AgGridReact>(null)
  const approverGroupsList = useReactiveVar(approverGroupsListVar)
  const selectedBackupApproverGroups = useReactiveVar(selectedBackupApproverGroupsVar)
  const activeSelectedBackupApproverGroups = useMemo(
    () => selectedBackupApproverGroups.filter((row: any) => row.isActive),
    [selectedBackupApproverGroups]
  )
  const hasUnsavedChanges = useReactiveVar(hasUnsavedChangesVar)
  const [selectedRows, setSelectedRows] = useState<any[]>([])
  const hasSelection = selectedRows.length > 0

  const { handleGetApproverGroupsByUser } = useGetApproverGroupsByUser()

  const { handleGetBackupApproversByUser } = useGetBackupApproverByUser()

  const { loadEmployees, isLoading, searchEmployees, clearSearch, setLoadEmployees } =
    useEmployeeLookupWithPhFlag()
  const [beginDate, setBeginDate] = useState(getTodayDateString())
  const [endDate, setEndDate] = useState('')
  const [searchValue, setSearchValue] = useState('')
  const [selectedEmployee, setSelectedEmployee] = useState<any>(null)
  const [showPhAccessAlert, setShowPhAccessAlert] = useState(false)
  const [alertMessage, setAlertMessage] = useState('')

  const onGridReady = useCallback((params: GridReadyEvent) => {
    setGridApi(params?.api)
  }, [])

  const onAssignmentsGridReady = useCallback((params: GridReadyEvent) => {
    setAssignmentsGridApi(params?.api)
  }, [])

  const getSelectedRowData = () => {
    const selectedData = gridApi?.getSelectedRows()
    const composedRows = addFieldsToRows(
      selectedData,
      selectedEmployee?.emailAddress,
      beginDate,
      endDate
    )
    return composedRows
  }

  const { user } = useAuthHook()
  const userEmail = user?.profile?.sub ?? ''

  useEffect(() => {
    const fetchData = async () => {
      if (!userEmail) return

      const input = { userEmail }
      const backupApproversInput = { primaryApproverEmail: userEmail }

      try {
        // Wait for the first request to finish
        await handleGetApproverGroupsByUser(input)

        // Call the second only after the first resolves
        await handleGetBackupApproversByUser(backupApproversInput)
        setHasUnsavedChanges(false)
      } catch (err) {
        console.error('Error in sequential requests:', err)
      }
    }

    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail])

  const handleBeginDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newBeginDate = e.target.value
    setBeginDate(newBeginDate)
    if (endDate) {
      if (shouldClearEndDate(newBeginDate, endDate, getMaxEndDate)) {
        setEndDate('')
      }
    }
  }

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEndDate = e.target.value
    if (beginDate && newEndDate) {
      const beginDateObj = parseISODate(beginDate)
      const endDateObj = parseISODate(newEndDate)
      if (endDateObj < beginDateObj) {
        setEndDate('')
        return
      }
    }
    setEndDate(newEndDate)
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchValue(value)
    searchEmployees(value, processSearchString, removeSpacesAfterComma)
  }

  const handleClearSearch = () => {
    setSearchValue('')
    clearSearch()
    setSelectedEmployee(null)
  }

  const handleEmployeeSelect = (employee: any) => {
    setSelectedEmployee(employee)
    setSearchValue(formatEmployeeDisplay(employee))
    setLoadEmployees([])
    if (employee.hasPhAccess === 'N') {
      setAlertMessage(
        `Assigned user doesn't have PriceHub access. Please make sure user gets access to PriceHub prior starting backup period.`
      )
      setShowPhAccessAlert(true)
    }
  }

  const onAssignClick = () => {
    const merged = [...selectedBackupApproverGroups, ...getSelectedRowData()]
    const unique = Array.from(new Map(merged.map((r) => [r.assignmentKey, r])).values())
    setSelectedApproverGroups(unique)
    setHasUnsavedChanges(true)
  }

  const onSelectionChanged = useCallback((e: SelectionChangedEvent) => {
    const rows = e.api.getSelectedRows()
    setSelectedRows(rows)
  }, [])

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsCheckboxChecked(event.target.checked)
  }

  const handleOkClick = async () => {
    setAlertQueue({
      type: MessageType.INFO,
      message: errorMessages.SAVE_ASSIGNMENT_WARNING,
      onClose: handleCancelClick,
      onOk: handleSaveAssignment,
    })
  }

  const handleSaveAssignment = async () => {
    const selectedBackupApproverGroupsInput = buildSelectedBackupApproverGroupsInput(
      selectedBackupApproverGroups,
      userEmail
    )

    try {
      await saveBackupApprovers({
        variables: {
          input: {
            sendEmails: isCheckboxChecked,
            backupApprovers: selectedBackupApproverGroupsInput,
          },
        },
      })
      const backupApproversInput = { primaryApproverEmail: userEmail }
      setAlertQueue({
        type: MessageType.SUCCESS,
        pauseAutoHideProp: true,
        message: `Assignments saved successfully`,
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        onOk: () => {},
      })
      await handleGetBackupApproversByUser(backupApproversInput)
      setHasUnsavedChanges(false)
    } catch (error) {
      console.error('Error saving backup approvers:', error)
      setAlertQueue({
        type: MessageType.ERROR,
        message: 'Failed to save assignments. Please try again.',
      })
      // Don't reset hasUnsavedChanges on error so user can retry
    }
  }
  const handleCancelClick = () => {
    setIsCheckboxChecked(false)
    // Reload data to discard unsaved changes
    const backupApproversInput = { primaryApproverEmail: userEmail }
    handleGetBackupApproversByUser(backupApproversInput)
    setHasUnsavedChanges(false)
  }
  const assignmentsFrameworkComponents = useMemo(
    () => ({
      ...assignmentsFrameworkComponentsBase,
      deleteCellRenderer: DeleteCellRenderer,
      deleteHeaderRenderer: DeleteHeaderRenderer,
    }),
    []
  )

  /**
   * Row selection configuration for AG Grid v32
   * - Enables multi-row selection with checkboxes
   * - Header checkbox only selects filtered rows (replaces deprecated headerCheckboxSelectionFilteredOnly)
   * - Checkboxes enabled at grid level (replaces deprecated column-level checkboxSelection)
   * - Disables row click selection (replaces deprecated suppressRowClickSelection)
   */
  const rowSelection = useMemo(
    () => ({
      mode: 'multiRow' as const,
      selectAll: 'filtered' as const,
      checkboxes: true,
      headerCheckbox: true,
      enableClickSelection: false,
    }),
    []
  )

  return (
    <div className={clsx(classes.mainContainer)}>
      {showPhAccessAlert && (
        <>
          <button
            className={classes.alertOverlay}
            onClick={() => setShowPhAccessAlert(false)}
            type="button"
            aria-label="Close alert"
          />
          <div className={classes.alertContainer}>
            <Alert
              type={MessageType.WARNING}
              id={1}
              message={alertMessage}
              autoHideTimeout={0}
              pauseAutoHideProp={true}
              onClose={() => setShowPhAccessAlert(false)}
              onOk={() => setShowPhAccessAlert(false)}
              pauseAutoHide={false}
            />
          </div>
        </>
      )}
      <Box className={classes.title}>Assign Backup</Box>
      <div className={classes.container}>
        {/* Modified responsive layout container */}
        <div className={classes.responsiveContentContainer}>
          <div className={classes.tableContainer}>
            <div className={classes.tableHeaderTitle} style={{ width: '100%' }}>
              Select Approver Group
            </div>
            <div
              className={clsx('ag-theme-alpine', classes.manageBackUpTableContainer)}
              style={{ height: 400, width: '100%' }}
            >
              <AgGridReact
                ref={gridRef}
                rowHeight={32}
                rowData={approverGroupsList}
                columnDefs={approverGroupColumnDefs}
                defaultColDef={defaultColDef}
                components={frameworkComponents}
                suppressContextMenu={true}
                rowSelection={rowSelection}
                onSelectionChanged={onSelectionChanged}
                onGridReady={(params) => onGridReady(params)}
              />
            </div>
          </div>

          <div className={classes.dateFieldsContainer}>
            <Box sx={{ mt: 2 }}>
              <div className={classes.searchTitle}>
                <div className={classes.selectUserText}>Select User</div>
                <Box className={classes.searchFieldContainer} sx={{ position: 'relative' }}>
                  <TextField
                    fullWidth
                    value={searchValue}
                    onChange={handleSearchChange}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchOutlined className={classes.searchIconBlue} />
                          </InputAdornment>
                        ),
                        endAdornment: searchValue && (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={handleClearSearch}>
                              <Close />
                            </IconButton>
                          </InputAdornment>
                        ),
                        disableUnderline: true,
                      },
                    }}
                    placeholder="Search for user's name or e-mail"
                    id="standard-bare"
                    variant="standard"
                    autoComplete="off"
                  />

                  {searchValue && loadEmployees.length > 0 && (
                    <Paper elevation={3} className={classes.searchDropdown}>
                      <List>
                        {loadEmployees.map((emp: any, idx: number) => (
                          <React.Fragment key={emp.id || `${emp.emailAddress}-${idx}`}>
                            <ListItem disablePadding>
                              <ListItemButton onClick={() => handleEmployeeSelect(emp)}>
                                <ListItemText
                                  className={classes.employeeListItemText}
                                  primary={`${toProperCase(emp.firstName)} ${toProperCase(
                                    emp.lastName
                                  )} - ${emp.emailAddress}`}
                                />
                              </ListItemButton>
                            </ListItem>
                            {idx < loadEmployees.length - 1 && (
                              <hr className={classes.employeeListDivider} />
                            )}
                          </React.Fragment>
                        ))}
                      </List>
                    </Paper>
                  )}
                  {searchValue && isLoading && (
                    <Paper elevation={3} className={classes.loadingDropdown}>
                      <Typography>Loading...</Typography>
                    </Paper>
                  )}
                </Box>
              </div>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Begin Date"
                  value={beginDate ? parseISODate(beginDate) : null}
                  onChange={(date) => {
                    const iso = date ? formatDateLocal(date) : ''
                    handleBeginDateChange({ target: { value: iso } } as any)
                  }}
                  minDate={parseISODate(getTodayDateString())}
                  format="dd/MM/yyyy"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small',
                      placeholder: 'dd/mm/yyyy',
                      sx: { mb: 2, '& .MuiInputBase-input': { fontSize: '14px' } },
                      inputProps: { readOnly: true },
                    },
                  }}
                  slots={{ openPickerIcon: ArrowDropDownIcon }}
                />
                <DatePicker
                  label="End Date"
                  value={endDate ? parseISODate(endDate) : null}
                  onChange={(date) => {
                    const iso = date ? formatDateLocal(date) : ''
                    handleEndDateChange({ target: { value: iso } } as any)
                  }}
                  disabled={!beginDate}
                  minDate={beginDate ? parseISODate(beginDate) : undefined}
                  maxDate={beginDate ? parseISODate(getMaxEndDate(beginDate)) : undefined}
                  format="dd/MM/yyyy"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small',
                      placeholder: 'dd/mm/yyyy',
                      InputLabelProps: { shrink: true },
                      sx: { '& .MuiInputBase-input': { fontSize: '14px' } },
                      inputProps: { readOnly: true },
                    },
                  }}
                  slots={{ openPickerIcon: ArrowDropDownIcon }}
                />
              </LocalizationProvider>
            </Box>
          </div>
        </div>
        <Box className={classes.buttonContainer}>
          <Button
            onClick={() => {
              onAssignClick()
            }}
            variant="outlined"
            color="primary"
            className={classes.assignToButton}
            startIcon={<ArrowDownwardIcon />}
            disabled={!hasSelection || !endDate || !selectedEmployee}
          >
            Assign
          </Button>
        </Box>
        <div className={classes.assignmentsTableContainer}>
          <div className={classes.tableHeaderTitle} style={{ width: '100%' }}>
            Assignments
          </div>
          <div
            className={clsx('ag-theme-alpine', classes.manageBackUpTableContainer)}
            style={{ height: 400, width: '100%' }}
          >
            <AgGridReact
              rowHeight={32}
              ref={assignmentsTableRef}
              rowData={activeSelectedBackupApproverGroups}
              columnDefs={assignmentsColumnsDefs}
              defaultColDef={defaultColDef}
              components={assignmentsFrameworkComponents}
              suppressContextMenu={true}
              rowSelection="single"
              onGridReady={(params) => onAssignmentsGridReady(params)}
              suppressScrollOnNewData={true}
            />
          </div>
        </div>
        <Box className={classes.saveAssignmentsButtonContainer}>
          {/* Checkbox and Buttons */}
          <FormControlLabel
            control={
              <Checkbox
                checked={isCheckboxChecked}
                onChange={handleCheckboxChange}
                color="primary"
              />
            }
            label="I want to send all pending requests/emails"
            sx={(theme) => ({
              position: 'absolute',
              left: 24,
              [theme.breakpoints.down('md')]: {
                position: 'static',
                marginBottom: 2,
              },
            })}
          />
          <Box
            sx={(theme) => ({
              display: 'flex',
              flex: '1 1 100%',
              justifyContent: 'center',
              gap: 2,
              [theme.breakpoints.between('md', 'lg')]: {
                justifyContent: 'end',
              },
            })}
          >
            <Button
              color="primary"
              variant="contained"
              data-testid="save-assignments-ok-btn"
              onClick={handleOkClick}
              sx={{ width: '150px' }}
              disabled={!hasUnsavedChanges}
            >
              OK
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleCancelClick}
              sx={{ width: '150px' }}
              disabled={!hasUnsavedChanges}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </div>
      <AgContextMenuContainer
        gridApi={assignmentsGridApi ?? undefined}
        columnHeaderRightClickMenuVar={isAssignmentsColumnHeaderRightClickMenuVar}
        columnDefs={assignmentsColumnsDefs}
        tableKey={TABLE_STATE_KEY}
        hideCustomizeColumns
      />
      <AgContextMenuContainer
        gridApi={gridApi ?? undefined}
        columnHeaderRightClickMenuVar={isColumnHeaderRightClickMenuVar}
        columnDefs={approverGroupColumnDefs}
        tableKey={TABLE_STATE_KEY}
        hideCustomizeColumns
      />
    </div>
  )
}

export default ManageBackUpPage
